import { useEffect, useId, useState } from "react";
import { cx } from "@/utils/cx";

/**
 * Gymkee 2027 crescent loader (spec: components/specs/crescent-loader.json, sheet DSLoading).
 *
 * The smile from the mark, drawing itself, for every wait whose end is not known.
 * Nothing in Gymkee spins.
 *
 * It used to be a *smile-shaped arc*: a symmetric curve with a uniform stroke, round caps, and
 * nothing to do with the logo beyond the idea of a smile. Two things were wrong with that, and Mo
 * named both on 5 September 2026. It was too thin — the stroke is set in viewBox units on a 24
 * box, so at 16 it rendered as a one-pixel line, and 16 is the size that sits inside every button
 * in the product. And it was not the mark's smile: the real one is a filled crescent, heavy at the
 * left and thinning as it sweeps right to a point, not a stroke of even width.
 *
 * So the path below is the mark's own smile, lifted verbatim from `LogoOrange` (its second path)
 * and only measured and moved into a 24 box: every control point keeps its relationship to every
 * other one, so the loader and the logo are the same curve rather than two drawings of one idea.
 *
 * Weight is optical, not fixed. A filled shape shrinks in both directions at once, so the smaller
 * boxes take a hairline of the same colour around the fill — it fattens the silhouette without
 * touching its shape. 48 needs none.
 *
 * The reveal changed with the shape. A stroke can draw itself through `stroke-dashoffset`; a fill
 * cannot, so the crescent is clipped by a rectangle that TRANSLATES across it, left to right, over
 * `motion.duration.slow`. That keeps the motion rule intact — transform and opacity only, never a
 * width or a position — and reads as the same gesture. Under `prefers-reduced-motion` the clip
 * rests where it uncovers the whole shape, so the smile is simply there.
 */

export type CrescentSize = 16 | 24 | 48;
export type CrescentTone = "tertiary" | "complete" | "onOrange" | "onInk";

export interface CrescentProps {
  /** 16 inside a button, 24 in a row, 48 in an empty region. */
  size?: CrescentSize;
  /** tertiary while waiting, complete (orange) for a finish, on-surface tones inside a fill. */
  tone?: CrescentTone;
  /** At 48 only: one line under the glyph, in the person's words. */
  label?: string;
  /** Milliseconds before the glyph enters. Under this, nothing is drawn at all. */
  delay?: number;
  /** True while waiting, false for the single draw that marks a finish. */
  repeat?: boolean;
  className?: string;
}

const TONE_COLOR: Record<CrescentTone, string> = {
  tertiary: "var(--gk-text-tertiary)",
  complete: "var(--gk-action-primary)",
  onOrange: "var(--gk-text-on-orange)",
  onInk: "var(--gk-text-on-ink)",
};

/**
 * The smile of the mark, fitted to a 24 box with the icon set's 2 of padding either side.
 * Generated from `LogoOrange`'s second path; see the note above.
 */
const SMILE =
  "M15.2256 11.6748C16.7206 11.2369 18.1303 10.5478 19.3945 9.6368C21.1316 8.3661 21.6945 7.2391 21.9462 7.407C22.1977 7.5747 21.6347 11.615 18.8195 14.1685C14.411 18.1847 7.8822 16.0746 7.403 15.9069C4.2404 14.8399 1.7247 12.502 2.0242 11.8427C2.3237 11.1833 5.1149 12.5261 10.1823 12.3583C11.8861 12.354 13.582 12.1243 15.2256 11.6748Z";

/**
 * How much hairline the fill takes so it still reads when the box shrinks. In viewBox units, so
 * 0.75 at a 16 box is half a pixel of extra weight on each edge — enough to stop the tapered end
 * disappearing, not enough to blunt it.
 */
const OPTICAL_WEIGHT: Record<CrescentSize, number> = {
  16: 0.75,
  24: 0.4,
  48: 0,
};

export function Crescent({
  size = 24,
  tone = "tertiary",
  label,
  delay = 300,
  repeat = true,
  className,
}: CrescentProps) {
  const [shown, setShown] = useState(delay <= 0);
  // One clip per instance: two loaders on a page must not share a reveal.
  const maskId = `gk-crescent-mask-${useId().replace(/[^\w-]/g, "")}`;

  useEffect(() => {
    if (delay <= 0) {
      setShown(true);
      return;
    }
    setShown(false);
    const timer = window.setTimeout(() => setShown(true), delay);
    return () => window.clearTimeout(timer);
  }, [delay]);

  if (!shown) {
    // The box is measured up front, so nothing around the glyph jumps when it enters.
    return (
      <span
        aria-hidden="true"
        className={cx("inline-block flex-none", className)}
        style={{ width: size, height: size }}
      />
    );
  }

  const weight = OPTICAL_WEIGHT[size];
  /*
    Orange means a thing finished, and a finish is drawn once. `tone` and `repeat` are separate
    props, so a caller could ask for both — and three did (`assessmentUpcoming`,
    `assessmentDuplicates`, `AiProgressModal`), each drawing a looping orange crescent, which is
    the one combination the spec bans. The atom settles it rather than trusting ninety call sites.
  */
  const loops = tone === "complete" ? false : repeat;

  const glyph = (
    <svg
      aria-hidden="true"
      className={cx(loops ? "ds-crescent" : "ds-crescent-once", "flex-none")}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      <clipPath id={maskId}>
        {/* A clip, not a mask: a mask rectangle would have to be painted pure white, because a
            mask reads luminance rather than colour, and a raw colour has no business in an inline
            svg (gate 1.1). A clip needs no paint at all. At rest it covers the whole box, so an
            element with no animation is a finished smile rather than an empty square. */}
        <rect className="ds-crescent-wipe" x="0" y="0" width="24" height="24" />
      </clipPath>
      <path
        d={SMILE}
        clipPath={`url(#${maskId})`}
        style={{
          fill: TONE_COLOR[tone],
          stroke: weight > 0 ? TONE_COLOR[tone] : undefined,
          strokeWidth: weight > 0 ? weight : undefined,
        }}
      />
    </svg>
  );

  if (size === 48 && label) {
    return (
      <span
        className={cx(
          "ds-crescent-enter inline-flex flex-col items-center gap-gk-2",
          className,
        )}
        role="status"
        aria-live="polite"
      >
        {glyph}
        <span
          className="text-center"
          style={{
            color: "var(--gk-text-secondary)",
            fontFamily: "var(--gk-font-text)",
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          {label}
        </span>
      </span>
    );
  }

  return (
    <span className={cx("ds-crescent-enter inline-flex flex-none", className)}>
      {glyph}
    </span>
  );
}
